import { Controller } from "@hotwired/stimulus"

// Controlador para el botón "Optimizar SEO" en posts de admin
// Dispara ContentUpdaterService via POST y hace polling del estado
// Muestra modales de confirmación si hay borrador o actualización reciente
export default class extends Controller {
  static values = {
    triggerUrl: String,
    statusUrl: String,
    historialUrl: String
  }

  static targets = ["button", "status"]

  static MAX_RETRIES = 120 // ~10 minutos a 5s
  static POLLING_INTERVAL = 5000

  connect() {
    this.retryCount = 0
    this.originalButtonHTML = this.buttonTarget.innerHTML
    this.verificarEstadoInicial()
  }

  disconnect() {
    this.detenerPolling()
  }

  async verificarEstadoInicial() {
    try {
      const response = await fetch(this.statusUrlValue)
      const data = await response.json()

      if (data.status === "pending") {
        this.mostrarProcesando()
        this.iniciarPolling()
      } else if (data.status === "completed") {
        this.mostrarCompletado()
      } else if (data.status === "error") {
        this.mostrarError()
      }
    } catch (_error) {
      // Estado inicial no disponible, mostrar botón normal
    }
  }

  async actualizar() {
    if (this.buttonTarget.disabled) return

    try {
      const response = await fetch(this.statusUrlValue)
      const data = await response.json()

      if (data.has_draft) {
        this.mostrarModalBorrador(data)
      } else if (data.recently_updated) {
        this.mostrarModalReciente(data)
      } else {
        this.ejecutarActualizacion()
      }
    } catch (_error) {
      this.ejecutarActualizacion()
    }
  }

  async ejecutarActualizacion() {
    this.detenerPolling()
    this.retryCount = 0
    this.mostrarProcesando()

    try {
      const response = await fetch(this.triggerUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({})
      })

      if (response.status === 202) {
        this.iniciarPolling()
      } else {
        this.mostrarError()
      }
    } catch (_error) {
      this.mostrarError()
    }
  }

  iniciarPolling() {
    this.pollingInterval = setInterval(() => {
      this.verificarEstado()
    }, this.constructor.POLLING_INTERVAL)
  }

  detenerPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  async verificarEstado() {
    if (this.retryCount >= this.constructor.MAX_RETRIES) {
      this.detenerPolling()
      this.mostrarTimeout()
      return
    }

    this.retryCount++

    try {
      const response = await fetch(this.statusUrlValue)
      const data = await response.json()

      if (data.status === "completed") {
        this.detenerPolling()
        this.mostrarCompletado()
      } else if (data.status === "error") {
        this.detenerPolling()
        this.mostrarError()
      }
    } catch (_error) {
      // Error de red temporal, continuar polling
    }
  }

  // --- Modales de confirmación ---

  mostrarModalReciente(data) {
    const fecha = this.formatearFecha(data.completed_at)
    const quien = this.escaparHtml(data.requested_by || "desconocido")

    this.crearModal({
      titulo: "Aviso: Artículo optimizado recientemente",
      cuerpo: `
        <p>Este artículo se optimizó hace menos de un mes.</p>
        <p><em>Última optimización: ${fecha}
        por <strong>${quien}</strong></em></p>
        <p>¿Estás seguro de querer optimizarlo de nuevo?</p>
      `
    })
  }

  mostrarModalBorrador(data) {
    const fecha = this.formatearFecha(data.completed_at || data.requested_at)
    const quien = this.escaparHtml(data.requested_by || "desconocido")

    this.crearModal({
      titulo: "Aviso: Borrador detectado",
      cuerpo: `
        <p>Este artículo contiene un borrador sin publicar:</p>
        <p><em>Borrador: ${fecha}
        por <strong>${quien}</strong></em></p>
        <p>Si continúas,
        <strong>el borrador sin publicar se perderá.</strong>
        <br>¿Estás seguro de que quieres continuar?</p>
      `
    })
  }

  crearModal({ titulo, cuerpo }) {
    const previo = document.getElementById("content-updater-modal")
    if (previo) previo.remove()

    const modal = document.createElement("dialog-modal")
    modal.id = "content-updater-modal"
    modal.setAttribute("closingx", "")
    modal.setAttribute("size", "medium")
    modal.innerHTML = `
      <dialog-content>
        <dialog-header has-title>
          <dialog-close action="dismiss"></dialog-close>
          <dialog-title style="text-transform: none;
            color: #1a1a1a; font-weight: 700">${titulo}</dialog-title>
        </dialog-header>
        <dialog-body>${cuerpo}</dialog-body>
        <dialog-actions>
          <dialog-action action="confirm" class="rnk-Button-cta"
            style="background: #00856f; border-color: #00856f;
            text-transform: none">Sí, optimizar</dialog-action>
          <dialog-action action="dismiss"
            class="rnk-Button-outline"
            style="text-transform: none">No optimizar</dialog-action>
        </dialog-actions>
      </dialog-content>
    `

    document.body.appendChild(modal)

    modal.addEventListener("click", (event) => {
      const action = event.target.getAttribute("action")
      if (action === "confirm") {
        modal.close(true)
        this.ejecutarActualizacion()
      }
    })

    modal.open()
  }

  escaparHtml(str) {
    const d = document.createElement("div")
    d.appendChild(document.createTextNode(str))
    return d.innerHTML
  }

  formatearFecha(isoString) {
    if (!isoString) return "\u2014"
    const d = new Date(isoString)
    return d.toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    })
  }

  // --- Estados visuales ---

  mostrarProcesando() {
    this.buttonTarget.disabled = true
    this.buttonTarget.innerHTML = '<span class="rnk-spinner"></span> Optimizando...'
    this.statusTarget.textContent = ""
  }

  mostrarCompletado() {
    this.buttonTarget.disabled = false
    this.buttonTarget.innerHTML = this.originalButtonHTML
  }

  mostrarError() {
    this.buttonTarget.disabled = false
    this.buttonTarget.innerHTML = this.originalButtonHTML
    this.statusTarget.textContent = "Error al optimizar"
  }

  mostrarTimeout() {
    this.buttonTarget.disabled = false
    this.buttonTarget.innerHTML = this.originalButtonHTML
    this.statusTarget.textContent = "Tiempo agotado"
  }
}
